import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CalendarCheck, Users, Compass } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * BottomNav — 3 onglets uniquement.
 *
 * Inspiré de Pipedrive / Apollo / Attio : un commercial a 3 jobs.
 *   1. Aujourd'hui — ce que je dois faire maintenant
 *   2. Mes prospects — qui je suis, où ils en sont
 *   3. Trouver — comment j'en trouve de nouveaux
 *
 * L'avatar top-right (sur chaque page) ouvre le profil/réglages.
 */
const items = [
  { to: "/dashboard", label: "Aujourd'hui",   icon: CalendarCheck, testid: "nav-today"     },
  { to: "/pipeline",  label: "Mes prospects", icon: Users,         testid: "nav-prospects" },
  { to: "/trouver",   label: "Trouver",       icon: Compass,       testid: "nav-find"      },
];

export default function BottomNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth() || {};

  const hide =
    pathname === "/" ||
    pathname.startsWith("/login") ||
    pathname.startsWith("/register") ||
    pathname.startsWith("/visit") ||
    pathname.startsWith("/phone") ||
    pathname.startsWith("/social") ||
    pathname.startsWith("/sprint") ||
    pathname.startsWith("/onboarding") ||
    pathname.startsWith("/billing");

  if (hide) return null;

  return (
    <>
      {/* Avatar top-right — accès profil/réglages global */}
      {user && (
        <button
          onClick={() => navigate("/settings")}
          data-testid="nav-profile-avatar"
          className="fixed top-3 right-3 z-40 w-9 h-9 rounded-full bg-[var(--surface)] border border-[var(--border)] grid place-items-center text-zinc-200 text-[12px] font-semibold hover:border-emerald-500/60 active:scale-95 transition-colors shadow-floating"
          aria-label="Profil et réglages"
        >
          {user.picture ? (
            <img src={user.picture} alt="" className="w-full h-full rounded-full object-cover" />
          ) : (
            (user.name || user.email || "?").charAt(0).toUpperCase()
          )}
        </button>
      )}

      <nav
        data-testid="bottom-nav"
        className="fixed bottom-0 left-0 right-0 z-50"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div
          className="absolute inset-0"
          style={{
            background: "rgba(9,9,11,0.92)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            borderTop: "1px solid var(--border)",
          }}
        />
        <div className="relative max-w-2xl mx-auto grid grid-cols-3">
          {items.map(({ to, label, icon: Icon, testid }) => {
            const isActive = pathname === to || (to !== "/dashboard" && pathname.startsWith(to));
            return (
              <button
                key={to}
                onClick={() => navigate(to)}
                data-testid={testid}
                className={`relative flex flex-col items-center justify-center py-3 gap-1 text-[11px] font-medium transition-colors active:scale-95 ${
                  isActive ? "text-white" : "text-zinc-500 hover:text-zinc-200"
                }`}
              >
                {isActive && (
                  <span
                    className="absolute inset-x-3 inset-y-1.5 rounded-xl"
                    style={{
                      background: "rgba(0,220,130,0.18)",
                      border: "1px solid rgba(0,220,130,0.45)",
                    }}
                  />
                )}
                <Icon
                  className={`relative w-5 h-5 ${isActive ? "text-emerald-300" : ""}`}
                  strokeWidth={isActive ? 2.2 : 1.7}
                />
                <span className={`relative tracking-wide ${isActive ? "font-semibold text-white" : ""}`}>
                  {label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
}
